import React, { useState } from "react";
import { db, auth } from "../../firebase";
import { collection, addDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { useTheme } from "../../context/ThemeContext";

export default function CreateSquad({ onClose }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const { isDarkMode } = useTheme();

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;

        setLoading(true);
        try {
            const currentUid = auth.currentUser.uid;
            // Generate a simple random text code for joining
            // In production, ensure uniqueness or use dynamic links
            const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

            await addDoc(collection(db, "squads"), {
                name,
                description,
                createdBy: currentUid,
                admins: [currentUid],
                members: [currentUid],
                inviteCode,
                isPublic: false,
                createdAt: serverTimestamp(),
                photoURL: "", // Optional: Add image upload logic later
                lastMessage: { text: "Squad created!" },
                lastMessageTimestamp: serverTimestamp()
            });

            onClose();
        } catch (error) {
            console.error("Error creating squad:", error);
            alert("Failed to create Squad.");
        } finally {
            setLoading(false);
        }
    };

    const bgClass = isDarkMode ? "bg-slate-800 text-white" : "bg-white text-gray-800";
    const inputClass = isDarkMode
        ? "bg-slate-700 border-slate-600 text-white placeholder-gray-400"
        : "bg-gray-50 border-gray-300 text-gray-900";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${bgClass} animate-fade-in-up`}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                        Create New Squad
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>

                <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1 opacity-80">Squad Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={`w-full rounded-lg px-4 py-3 border focus:ring-2 focus:ring-purple-500 outline-none transition-all ${inputClass}`}
                            placeholder="e.g. Gamers Unite"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1 opacity-80">Description (Optional)</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className={`w-full rounded-lg px-4 py-3 border focus:ring-2 focus:ring-purple-500 outline-none transition-all resize-none h-24 ${inputClass}`}
                            placeholder="What's this squad about?"
                        />
                    </div>

                    <div className="pt-4 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`px-5 py-2.5 rounded-xl font-medium transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold shadow-lg hover:shadow-purple-500/30 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? "Creating..." : "Create Squad"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
